import qs from 'qs';

import { isNullOrUnDef } from './is';

/**
 * 将对象作为参数添加到 URL 中
 * @param baseUrl 基础 URL
 * @param obj 要添加到 URL 的参数对象
 * @returns 返回添加了参数的 URL
 *
 * 使用示例:
 *  - let obj = { a: '3', b: '4', c: null, d: undefined }
 *  - setObjToUrlParams('www.baidu.com', obj)
 *  - ==> 'www.baidu.com?a=3&b=4'
 */
export function setObjToUrlParams(baseUrl: string, obj: any, encode = true): string {
  let parameters = '';
  for (const key in obj) {
    // 如果键值对存在，并且值不是 null 或 undefined，将其添加到参数字符串中
    if (Object.prototype.hasOwnProperty.call(obj, key) && !isNullOrUnDef(obj[key])) {
      // 由于参数可能包含特殊字符，导致生成无效的 URL，默认对参数进行编码
      parameters += key + '=' + (encode ? encodeURIComponent(obj[key]) : obj[key]) + '&';
    }
  }
  parameters = parameters.replace(/&$/, '');

  // 检查 baseUrl 是否包含问号
  if (/\?/.test(baseUrl)) {
    // 如果 baseUrl 以问号或者&结尾，直接拼接参数
    if (/[?&]$/.test(baseUrl)) {
      return baseUrl + parameters;
    } else {
      // 否则在参数前添加&
      return baseUrl + '&' + parameters;
    }
  } else {
    // 如果 baseUrl 不包含问号，添加问号和参数
    return baseUrl.replace(/\/?$/, '?') + parameters;
  }
}

/**
 * 获取 URL 中的所有查询参数
 * @param url 可选参数，指定要解析的 URL，不传则解析当前页面 URL
 * @returns 返回包含所有查询参数的对象，支持通过泛型指定返回值类型
 *
 * 使用示例:
 *  - getUrlParams() // 获取当前 URL 的所有参数
 *  - getUrlParams('https://example.com?a=1&b=2') // 获取指定 URL 的参数 ==> { a: '1', b: '2' }
 *  - getUrlParams('https://example.com?ids[]=1&ids[]=2') // 支持数组 ==> { ids: ['1', '2'] }
 *  - interface QueryParams { id: string; name: string }
 *  - getUrlParams<QueryParams>() // 返回类型为 Partial<QueryParams>
 */
export function getUrlParams<T extends Record<string, any> = Record<string, string>>(url?: string): T {
  // 确定要解析的 URL
  const targetUrl = url || (typeof window !== 'undefined' ? window.location.href : '');

  if (!targetUrl) {
    return {} as T;
  }

  // 提取查询字符串：先去掉 hash，然后提取 ? 之后的部分
  // 支持以下格式：
  // - 'https://example.com?a=1&b=2'
  // - 'https://example.com?a=1&b=2#hash'
  // - '?a=1&b=2'
  // - 'a=1&b=2'
  const urlWithoutHash = targetUrl.split('#')[0];
  const queryStartIndex = urlWithoutHash.indexOf('?');

  // 提取查询字符串
  let queryString = '';
  if (queryStartIndex >= 0) {
    // 存在 ?，提取其后的内容
    queryString = urlWithoutHash.slice(queryStartIndex + 1);
  } else if (urlWithoutHash.includes('=')) {
    // 不存在 ?，但包含 =，说明整个字符串就是查询参数
    queryString = urlWithoutHash;
  }

  if (!queryString) {
    return {} as T;
  }

  // 使用 qs 解析查询参数
  const params = qs.parse(queryString, {
    ignoreQueryPrefix: true, // 忽略前导的 ? (处理边界情况)
    decoder: decodeURIComponent, // 解码器可以直接传函数引用
  });

  return params as T;
}

/**
 * 获取 URL 中指定的查询参数值
 * @param key 参数名
 * @param url 可选参数，指定要解析的 URL，不传则解析当前页面 URL
 * @returns 返回指定参数的值，不存在则返回 null
 *
 * 使用示例:
 *  - getUrlParam('id') // 获取当前 URL 中 id 参数的值
 *  - getUrlParam('name', 'https://example.com?name=test') // 获取指定 URL 中 name 参数的值 ==> 'test'
 */
export function getUrlParam(key: string, url?: string): string | null {
  const params = getUrlParams(url);
  return params[key] || null;
}
